import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bot, X, Send, Loader2, Sparkles, Target, FileText, BarChart3, MessageSquare } from 'lucide-react';

const QUICK_ACTIONS = [
  { action: 'pipeline_insights', label: 'Analizar Pipeline', icon: BarChart3, color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/10' },
];

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async (action = 'custom', contextId = '', text = '') => {
    const userText = text || input.trim();
    if (!userText && action === 'custom') return;
    if (action === 'custom') setMessages(prev => [...prev, { role: 'user', text: userText }]);
    setInput('');
    setLoading(true);
    try {
      const r = await api.post('/ai/assistant', { action, context_id: contextId, message: userText });
      setMessages(prev => [...prev, { role: 'assistant', text: r.data.response, action: r.data.action }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Error al procesar. Intenta de nuevo.', error: true }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // XSS: este texto se inyecta con dangerouslySetInnerHTML mas abajo. Antes se
  // pasaba crudo, asi que bastaba con que un lead escribiera HTML en su nombre o
  // sus notas para que, cuando la IA lo citara en su respuesta, ese codigo se
  // ejecutara en la sesion del vendedor (XSS almacenado, no solo self-XSS).
  // Se escapa PRIMERO el contenido y solo despues se aplican las marcas de
  // formato, que son nuestras y por lo tanto seguras.
  const escapeHtml = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatMessage = (text) => {
    return escapeHtml(text)
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  // Portado a document.body: un `position: fixed` sigue siendo relativo a un ancestro
  // transformado/con filter en vez del viewport (spec CSS), asi que montar aqui evita
  // depender de que ningun padre en el arbol de rutas tenga transform/filter/will-change.
  if (!open) {
    return createPortal(
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] shadow-lg shadow-[#3B82F6]/30 flex items-center justify-center hover:scale-110 active:scale-95"
        style={{ transition: 'transform 0.2s ease' }}
        data-testid="ai-assistant-btn"
      >
        <Bot className="w-6 h-6 text-black" />
      </button>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed bottom-6 right-6 z-50 w-96 h-[520px] rounded-2xl bg-[#0B0C10] border border-white/10 shadow-2xl shadow-black/50 flex flex-col overflow-hidden" style={{ animation: 'fadeInUp 0.2s ease' }} data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="p-4 border-b border-white/5 flex items-center justify-between shrink-0 bg-[#0B0C10]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#3B82F6]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white font-heading">Asistente IA</p>
            <p className="text-[10px] text-muted-foreground">Coach de ventas inmobiliarias</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-white" data-testid="ai-close-btn"><X className="w-5 h-5" /></button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Bot className="w-10 h-10 mx-auto text-[#3B82F6]/30 mb-3" />
            <p className="text-sm text-white/60 mb-1">Hola, soy tu asistente IA</p>
            <p className="text-xs text-muted-foreground mb-4">Puedo analizar leads, recomendar estrategias, generar descripciones y mas</p>
            <div className="space-y-2">
              {QUICK_ACTIONS.map(qa => (
                <button
                  key={qa.action}
                  onClick={() => sendMessage(qa.action, '', qa.label)}
                  className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 hover:border-white/15 text-left"
                  style={{ transition: 'border-color 0.2s ease' }}
                  data-testid={`ai-quick-${qa.action}`}
                >
                  <div className={`w-7 h-7 rounded-lg ${qa.bg} flex items-center justify-center shrink-0`}>
                    <qa.icon className={`w-3.5 h-3.5 ${qa.color}`} />
                  </div>
                  <span className="text-xs text-white/70">{qa.label}</span>
                </button>
              ))}
              <button onClick={() => sendMessage('custom', '', 'Dame consejos para cerrar mas ventas este mes')} className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 hover:border-white/15 text-left" style={{ transition: 'border-color 0.2s ease' }}>
                <div className="w-7 h-7 rounded-lg bg-blue-400/10 flex items-center justify-center shrink-0"><Target className="w-3.5 h-3.5 text-blue-400" /></div>
                <span className="text-xs text-white/70">Tips para cerrar ventas</span>
              </button>
              <button onClick={() => sendMessage('custom', '', 'Como puedo mejorar mi tasa de conversion de leads?')} className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 hover:border-white/15 text-left" style={{ transition: 'border-color 0.2s ease' }}>
                <div className="w-7 h-7 rounded-lg bg-purple-400/10 flex items-center justify-center shrink-0"><MessageSquare className="w-3.5 h-3.5 text-purple-400" /></div>
                <span className="text-xs text-white/70">Mejorar conversion de leads</span>
              </button>
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2.5 rounded-xl text-xs leading-relaxed ${
              msg.role === 'user'
                ? 'bg-primary/20 text-white rounded-br-sm'
                : msg.error
                  ? 'bg-red-500/10 text-red-400 rounded-bl-sm'
                  : 'bg-white/[0.04] text-white/80 rounded-bl-sm border border-white/5'
            }`}>
              <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }} />
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/[0.04] border border-white/5 rounded-xl rounded-bl-sm px-3 py-2.5 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 text-[#3B82F6] animate-spin" />
              <span className="text-xs text-muted-foreground">Analizando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/5 shrink-0 bg-[#0B0C10]">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pregunta algo..."
            className="bg-black/30 border-white/10 focus:border-[#3B82F6] text-sm h-9"
            data-testid="ai-input"
          />
          <Button onClick={() => sendMessage()} disabled={loading || !input.trim()} size="sm" className="h-9 w-9 p-0 bg-[#3B82F6] hover:bg-[#3B82F6]/80 shrink-0" data-testid="ai-send-btn">
            <Send className="w-4 h-4 text-black" />
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
